import { Group } from "@visx/group";
import { scaleBand, scaleLinear } from "@visx/scale";
import { Bar } from "@visx/shape";

import { formatCompactWholeNumberWithUnit, formatNumberToShort } from "@/web/lib/formatting";

const CHART_WIDTH = 340;
const CHART_HEIGHT = 232;
const MARGIN = { top: 26, right: 10, bottom: 44, left: 48 };
const TICK_COUNT = 6;
const MIN_STUB_PX = 4;
const DEFAULT_AXIS_MAX = 3_000_000;

type NegotiateValuationChartProps = {
  compsEstimate: number | undefined;
  /**
   * WITH.CO's own valuation for the property, when we have one (today: the
   * opening price an admin seeds on an invited-brand draft). `undefined` keeps
   * the bar in its original TBD placeholder state.
   */
  ourValuation: number | undefined;
  userMaxPrice: number | undefined;
};

function niceMax(values: readonly number[]): number {
  const rawMax = Math.max(...values, 0);
  if (rawMax <= 0) return DEFAULT_AXIS_MAX;

  const magnitude = 10 ** Math.floor(Math.log10(rawMax));
  const normalized = rawMax / magnitude;
  const niceNormalized = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  return niceNormalized * magnitude;
}

function formatDollarsShort(value: number): string {
  return formatCompactWholeNumberWithUnit(
    formatNumberToShort(value, { showThousands: true }),
    "Dollars",
  );
}

const CATEGORIES = [
  { key: "comps", lines: ["Comps"] as const },
  { key: "our", lines: ["Our", "Valuation"] as const },
  { key: "appraisal", lines: ["3p", "Appraisal"] as const },
  { key: "yourMax", lines: ["Your", "Max"] as const },
] as const;

export function NegotiateValuationChart({
  compsEstimate,
  ourValuation,
  userMaxPrice,
}: NegotiateValuationChartProps) {
  const xMax = CHART_WIDTH - MARGIN.left - MARGIN.right;
  const yMax = CHART_HEIGHT - MARGIN.top - MARGIN.bottom;

  const hasNumericScale =
    (compsEstimate != null && compsEstimate > 0) ||
    (ourValuation != null && ourValuation > 0) ||
    (userMaxPrice != null && userMaxPrice > 0);
  const yDomainMax = hasNumericScale
    ? niceMax([compsEstimate ?? 0, ourValuation ?? 0, userMaxPrice ?? 0])
    : DEFAULT_AXIS_MAX;

  const xScale = scaleBand<(typeof CATEGORIES)[number]["key"]>({
    domain: CATEGORIES.map((c) => c.key),
    range: [0, xMax],
    paddingInner: 0.42,
    paddingOuter: 0.28,
  });

  const yScale = scaleLinear<number>({
    domain: [0, yDomainMax],
    range: [yMax, 0],
    nice: true,
  });

  const ticks = yScale.ticks(TICK_COUNT);

  const compsValue = compsEstimate != null && compsEstimate > 0 ? compsEstimate : 0;
  const ourValue = ourValuation != null && ourValuation > 0 ? ourValuation : 0;
  const maxValue = userMaxPrice != null && userMaxPrice > 0 ? userMaxPrice : 0;

  const measureLabelWidth = (text: string, fontSizePx: number) =>
    Math.max(40, text.length * (fontSizePx * 0.55) + 14);

  return (
    <svg
      role="img"
      aria-label="Purchase price comparison"
      viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
      className="h-auto w-full overflow-visible text-withus-dark-blue"
    >
      <Group top={MARGIN.top} left={MARGIN.left}>
        <line x1={0} x2={0} y1={0} y2={yMax} stroke="currentColor" strokeOpacity={0.1} />
        <line x1={0} x2={xMax} y1={yMax} y2={yMax} stroke="currentColor" strokeOpacity={0.1} />

        {ticks.map((tick) => (
          <text
            key={tick}
            x={-8}
            y={yScale(tick)}
            dy="0.35em"
            textAnchor="end"
            className="fill-current text-[0.6875rem] opacity-60"
          >
            {formatDollarsShort(tick)}
          </text>
        ))}

        {CATEGORIES.map((cat) => {
          const barX = xScale(cat.key) ?? 0;
          const barWidth = xScale.bandwidth();
          const centerX = barX + barWidth / 2;

          const mutedBarFill = "color-mix(in srgb, var(--withus-dark-blue) 22%, transparent)";

          let barFill = "var(--withus-dark-blue)";
          let valueForScale = 0;
          let pillText = "TBD";
          let pillVariant: "compsValue" | "tbdDark" | "tbdMuted" | "yourMaxValue" | "yourMaxTbd" =
            "tbdDark";

          if (cat.key === "comps") {
            valueForScale = compsValue;
            if (compsValue > 0) {
              pillText = formatDollarsShort(compsValue);
              pillVariant = "compsValue";
            } else {
              pillVariant = "tbdDark";
              barFill = mutedBarFill;
            }
          } else if (cat.key === "our") {
            valueForScale = ourValue;
            if (ourValue > 0) {
              pillText = formatDollarsShort(ourValue);
              pillVariant = "compsValue";
            } else {
              pillVariant = "tbdMuted";
              barFill = mutedBarFill;
            }
          } else if (cat.key === "appraisal") {
            valueForScale = 0;
            pillVariant = "tbdDark";
          } else {
            valueForScale = maxValue;
            if (maxValue > 0) {
              pillText = formatDollarsShort(maxValue);
              pillVariant = "yourMaxValue";
              barFill = "var(--withus-red)";
            } else {
              pillVariant = "yourMaxTbd";
              barFill = "var(--withus-red)";
            }
          }

          const scaledTop = valueForScale > 0 ? yScale(valueForScale) : yMax;
          const rawHeight = yMax - scaledTop;
          const barHeight = valueForScale > 0 && rawHeight >= MIN_STUB_PX ? rawHeight : MIN_STUB_PX;
          const barY = yMax - barHeight;

          const pillW = measureLabelWidth(pillText, 12);
          const pillH = pillVariant === "tbdMuted" ? 18 : 20;
          const pillY = Math.max(0, barY - pillH - 4);

          const labelIsYourMax = cat.key === "yourMax";
          const lineClass = labelIsYourMax
            ? "fill-withus-red text-[0.625rem] font-semibold"
            : "fill-current text-[0.625rem] font-semibold opacity-60";

          return (
            <Group key={cat.key}>
              <Bar x={barX} y={barY} width={barWidth} height={barHeight} fill={barFill} />

              {pillVariant === "compsValue" && (
                <g>
                  <rect
                    x={centerX - pillW / 2}
                    y={pillY}
                    width={pillW}
                    height={pillH}
                    fill="var(--withus-dark-blue)"
                    stroke="var(--withus-dark-blue)"
                  />
                  <text
                    x={centerX}
                    y={pillY + pillH / 2}
                    dy="0.35em"
                    textAnchor="middle"
                    className="fill-withus-white text-xs font-semibold"
                  >
                    {pillText}
                  </text>
                </g>
              )}

              {pillVariant === "tbdDark" && (
                <g>
                  <rect
                    x={centerX - pillW / 2}
                    y={pillY}
                    width={pillW}
                    height={pillH}
                    fill="var(--withus-dark-blue)"
                    stroke="var(--withus-dark-blue)"
                  />
                  <text
                    x={centerX}
                    y={pillY + pillH / 2}
                    dy="0.35em"
                    textAnchor="middle"
                    className="fill-withus-white text-xs font-semibold"
                  >
                    {pillText}
                  </text>
                </g>
              )}

              {pillVariant === "tbdMuted" && (
                <g>
                  <rect
                    x={centerX - pillW / 2}
                    y={pillY}
                    width={pillW}
                    height={pillH}
                    fill={mutedBarFill}
                  />
                  <text
                    x={centerX}
                    y={pillY + pillH / 2}
                    dy="0.35em"
                    textAnchor="middle"
                    className="fill-withus-white text-[0.625rem] font-semibold"
                  >
                    {pillText}
                  </text>
                </g>
              )}

              {pillVariant === "yourMaxValue" && (
                <g>
                  <rect
                    x={centerX - pillW / 2}
                    y={pillY}
                    width={pillW}
                    height={pillH}
                    fill="var(--withus-white)"
                    stroke="var(--withus-red)"
                  />
                  <text
                    x={centerX}
                    y={pillY + pillH / 2}
                    dy="0.35em"
                    textAnchor="middle"
                    className="fill-withus-red text-xs font-semibold"
                  >
                    {pillText}
                  </text>
                </g>
              )}

              {pillVariant === "yourMaxTbd" && (
                <g>
                  <rect
                    x={centerX - pillW / 2}
                    y={pillY}
                    width={pillW}
                    height={pillH}
                    fill="var(--withus-white)"
                    stroke="var(--withus-red)"
                  />
                  <text
                    x={centerX}
                    y={pillY + pillH / 2}
                    dy="0.35em"
                    textAnchor="middle"
                    className="fill-withus-red text-xs font-semibold"
                  >
                    {pillText}
                  </text>
                </g>
              )}

              {cat.lines.map((line, lineIndex) => (
                <text
                  key={`${cat.key}-${line}`}
                  x={centerX}
                  y={yMax + 14 + lineIndex * 11}
                  textAnchor="middle"
                  className={lineClass}
                >
                  {line}
                </text>
              ))}
            </Group>
          );
        })}
      </Group>
    </svg>
  );
}
